import { generateColors } from "@mantine/colors-generator";
import {
  ActionIcon,
  Button,
  Checkbox,
  createTheme,
  Dialog,
  Group,
  LoadingOverlay,
  MantineProvider,
  mergeThemeOverrides,
  Modal,
  Select,
  Stack,
  Text,
  TextInput,
  Title
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { WarningIcon } from "@phosphor-icons/react";
import { createFileRoute, Outlet, useRouter } from "@tanstack/react-router";
import { useEffect } from "react";
import z from "zod";

import { Toolbar } from "@/components/@custom/Toolbar";
import { useSession } from "@/hooks/@custom/useSession";
import { MODAL_HANDLER } from "@/lib/Mantine";
import { Layout } from "@/pages/_layouts";
import { Role } from "@/store";
import { useGetAttendancesTodayByCompanyQuery } from "@/store/apis/nextcargo";
import { useSyncExpeditionQuery } from "@/store/apis/nextcargo/_custom/sync";
import { EXPEDITION_SYNC_POOLING_INTERVAL } from "@/store/apis/nextcargo/_custom/sync/sync.constants";
import { cn } from "@/utils/cn";
import { requireRoles } from "@/utils/requiredRoles";

const blue = generateColors("#228be6");
const theme = createTheme({
  primaryColor: "c-blue",
  defaultRadius: "sm",
  components: {
    Button: Button.extend({
      defaultProps: {
        size: "sm",
        radius: "sm"
      }
    }),
    TextInput: TextInput.extend({
      defaultProps: {
        classNames: {
          label: "!mb-0"
        },
        variant: "default"
      }
    }),
    Modal: Modal.extend({
      defaultProps: {
        keepMounted: false
      }
    })
  },
  colors: {
    "c-blue": blue
  }
});
const disabledTheme = createTheme({
  components: {
    Button: Button.extend({
      defaultProps: {
        disabled: true
      }
    }),
    ActionIcon: ActionIcon.extend({
      defaultProps: {
        disabled: true
      }
    }),
    TextInput: TextInput.extend({
      defaultProps: {
        disabled: true,
        readOnly: true
      }
    }),
    Select: Select.extend({
      defaultProps: {
        disabled: true
      }
    }),
    Checkbox: Checkbox.extend({
      defaultProps: {
        disabled: true
      }
    })
  }
});
const themeWithDisabled = mergeThemeOverrides(theme, disabledTheme);

const searchParamsSchema = z.object({
  shippingCompanyIdEncrypted: z.string().optional()
});

export const Route = createFileRoute("/expedicao")({
  component: RouteComponent,
  validateSearch: searchParamsSchema,
  beforeLoad: requireRoles({ allowed: [Role.Expedicao, Role.Espectador, Role.Portaria], fallback: "/expedicao/login" }),
  head: () => ({
    meta: [
      {
        title: "Expedição"
      }
    ]
  })
});

function RouteComponent() {
  const router = useRouter();

  const { expedition } = useSession();
  const expeditionCompanyId = expedition?.company.id;
  const expeditionRole = expedition?.role;
  const isExpeditionPath = router.history.location.pathname.startsWith("/expedicao");

  const { isLoading, isFetching } = useSyncExpeditionQuery(undefined, { pollingInterval: EXPEDITION_SYNC_POOLING_INTERVAL, skipPollingIfUnfocused: true });
  const { data: attendance } = useGetAttendancesTodayByCompanyQuery({ id: expeditionCompanyId! }, { skip: !expeditionCompanyId });

  const startedAttendance = !!attendance?.isInAttendance;
  const isMobile = useMediaQuery("(max-width: 768px)");
  const themeState = !isFetching && startedAttendance ? theme : themeWithDisabled;

  useEffect(() => {
    if (!isLoading && isExpeditionPath) {
      MODAL_HANDLER.expeditions.connection.status({ isOnline: true });
    }
  }, [isLoading, isExpeditionPath]);

  if (isLoading) return <LoadingOverlay visible loaderProps={{ type: "bars", size: "xl", color: "primary" }} overlayProps={{ backgroundOpacity: 1 }} />;

  return (
    <MantineProvider theme={themeState}>
      <Toolbar.Expedition />

      <Layout.Base
        slots={{
          main: {
            className: cn("pb-6", { "pt-40": !isMobile || (Role.Espectador !== expeditionRole && Role.Portaria !== expeditionRole) })
          },
          container: {
            className: cn("max-w-3xl")
          }
        }}
        align="start"
      >
        <Outlet />

        <Dialog opened={!startedAttendance} shadow="md" size="lg" radius="md" withCloseButton={false}>
          <Stack gap="xs">
            <Group align="center">
              <ActionIcon color="yellow" radius="xl" size="lg" variant="light" disabled={false}>
                <WarningIcon weight="fill" />
              </ActionIcon>

              <Title order={3} c="yellow">
                ATENÇÃO
              </Title>
            </Group>

            <Text lh="normal">Antes de realizar quaisquer ações você precisa iniciar o atendimento!</Text>
          </Stack>
        </Dialog>
      </Layout.Base>
    </MantineProvider>
  );
}
